/**
 * DriveAutoOnSetting — Settings → Cloud Storage: "Turn on Google Drive
 * automatically for agents that allow it". OFF by default.
 *
 * Each person switches Google Drive on for an agent from its conversation;
 * with this on, every agent that allows Drive has it on for them without a
 * click. A choice they made for one agent still stands, both ways
 * (`agent_drive_switch.py`). Without a connection there is nothing to turn
 * on, so the switch waits for one. Lives under the Google Drive row of
 * CloudStorageConnect, which owns the connection state.
 */

import { useEffect, useId, useState } from 'react';
import { Switch } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { getDriveAutoOn, setDriveAutoOn } from '../services/agentDriveSwitchService';
import './DriveAutoOnSetting.css';

export function DriveAutoOnSetting({ connected }: { connected: boolean }) {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const labelId = useId();
  const descId = useId();

  useEffect(() => {
    let alive = true;
    getDriveAutoOn().then((on) => { if (alive) setEnabled(on); }).catch(() => { if (alive) setEnabled(false); });
    return () => { alive = false; };
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    setFailed(false);
    try {
      setEnabled(await setDriveAutoOn(next));
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`drive-auto-on ${enabled ? 'is-on' : ''}`} data-testid="drive-auto-on">
      <div className="drive-auto-on__text">
        <span className="drive-auto-on__label" id={labelId}>{t('cloudStorage.autoOn.label')}</span>
        <span className="drive-auto-on__desc" id={descId}>
          {connected ? t('cloudStorage.autoOn.description') : t('cloudStorage.autoOn.needsConnection')}
        </span>
        {failed && <span className="drive-auto-on__error" role="status">{t('cloudStorage.autoOn.saveFailed')}</span>}
      </div>
      <Switch
        checked={!!enabled}
        loading={enabled === null || saving}
        disabled={!connected && !enabled}
        onChange={toggle}
        aria-labelledby={labelId}
        aria-describedby={descId}
        data-testid="drive-auto-on-switch"
      />
    </div>
  );
}
